import React, { useEffect, useState } from 'react';
import AppShell from '../components/AppShell';
import api from '../lib/api';
import { errMsg } from '../lib/errors';
import { LoadingBlock, EmptyState } from '../components/States';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { DatabaseBackup, RotateCcw, AlertTriangle, ShieldCheck, Trash2, Download, HardDrive, RefreshCw, Loader2, CheckCircle2, XCircle } from 'lucide-react';
import { toast } from 'sonner';

function bytesHuman(n) {
    if (!n) return '0 B';
    const u = ['B', 'KB', 'MB', 'GB']; let i = 0; let v = n;
    while (v >= 1024 && i < u.length - 1) { v /= 1024; i++; }
    return `${v.toFixed(1)} ${u[i]}`;
}
function timeAgo(iso) {
    if (!iso) return '—';
    const d = new Date(iso); const s = Math.floor((Date.now() - d.getTime()) / 1000);
    if (s < 60) return `${s}s ago`;
    if (s < 3600) return `${Math.floor(s / 60)}m ago`;
    if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
    return `${Math.floor(s / 86400)}d ago`;
}

export default function SystemAdmin() {
    const [status, setStatus] = useState(null);
    const [backups, setBackups] = useState([]);
    const [preview, setPreview] = useState(null);
    const [loading, setLoading] = useState(true);
    const [backingUp, setBackingUp] = useState(false);
    const [restoring, setRestoring] = useState(null);
    const [resetDialog, setResetDialog] = useState(false);
    const [restoreDialog, setRestoreDialog] = useState(null);
    const [resetForm, setResetForm] = useState({ reset_master: true, reset_transactions: true, confirm_phrase: '' });
    const [restorePhrase, setRestorePhrase] = useState('');

    const load = async () => {
        setLoading(true);
        try {
            const [s, b, p] = await Promise.all([
                api.get('/system/status'),
                api.get('/system/backups', { params: { limit: 30 } }),
                api.get('/system/reset/preview'),
            ]);
            setStatus(s.data); setBackups(b.data.items || []); setPreview(p.data);
        } catch (e) { toast.error(errMsg(e, 'Failed to load')); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    const doBackup = async () => {
        setBackingUp(true);
        try {
            const r = await api.post('/system/backup');
            toast.success(`Backup created — ${bytesHuman(r.data.size_bytes)}, ${r.data.docs_count} docs`);
            await load();
        } catch (e) { toast.error(errMsg(e, 'Backup failed')); }
        finally { setBackingUp(false); }
    };

    const doDelete = async (b) => {
        if (!window.confirm(`Delete backup ${b.file_name}? This cannot be undone.`)) return;
        try { await api.delete(`/system/backups/${b.id}`); toast.success('Deleted'); await load(); }
        catch (e) { toast.error(errMsg(e, 'Delete failed')); }
    };

    const doRestore = async () => {
        if (!restoreDialog) return;
        setRestoring(restoreDialog.id);
        try {
            const r = await api.post('/system/backups/restore', { backup_id: restoreDialog.id, confirm_phrase: restorePhrase });
            const count = Object.values(r.data.restored || {}).reduce((a, b) => a + b, 0);
            toast.success(`Restored ${count} documents across ${Object.keys(r.data.restored || {}).length} collections`);
            setRestoreDialog(null); setRestorePhrase(''); await load();
        } catch (e) { toast.error(errMsg(e, 'Restore failed')); }
        finally { setRestoring(null); }
    };

    const doReset = async () => {
        try {
            const r = await api.post('/system/reset', resetForm);
            toast.success(`Reset complete — safety backup saved (${bytesHuman(r.data.safety_backup.size_bytes)})`);
            setResetDialog(false); setResetForm(f => ({ ...f, confirm_phrase: '' })); await load();
        } catch (e) { toast.error(errMsg(e, 'Reset failed')); }
    };

    if (loading) return <AppShell pageTitle="System Administration"><LoadingBlock /></AppShell>;

    const hs = status?.hours_since_last_backup;
    const healthColor = status?.is_healthy ? 'green' : (hs == null ? 'red' : 'amber');

    return (
        <AppShell pageTitle="System Administration" pageActions={
            <Button variant="outline" onClick={load} data-testid="sys-refresh"><RefreshCw className="h-4 w-4 mr-2" />Refresh</Button>
        }>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
                <Card className="p-4" data-testid="sys-card-last-backup">
                    <div className="flex items-center gap-2 mb-1">
                        <DatabaseBackup className="h-4 w-4 text-neutral-500" />
                        <div className="text-xs uppercase tracking-wider font-semibold text-neutral-500">Last backup</div>
                    </div>
                    <div className="text-xl font-bold">{status?.last_backup?.file_name ? timeAgo(status.last_backup.created_at) : 'Never'}</div>
                    <div className={`text-xs mt-1 font-medium ${healthColor === 'green' ? 'text-green-700' : healthColor === 'amber' ? 'text-amber-700' : 'text-red-700'}`}>
                        {healthColor === 'green' ? '● Healthy' : healthColor === 'amber' ? '● Backup is stale' : '● No backup yet'}
                    </div>
                    {status?.last_backup && (
                        <div className="text-xs text-neutral-500 mt-1">{status.last_backup.file_name} · {bytesHuman(status.last_backup.size_bytes)} · {status.last_backup.docs_count} docs</div>
                    )}
                </Card>
                <Card className="p-4" data-testid="sys-card-next-backup">
                    <div className="flex items-center gap-2 mb-1">
                        <RefreshCw className="h-4 w-4 text-neutral-500" />
                        <div className="text-xs uppercase tracking-wider font-semibold text-neutral-500">Next auto backup</div>
                    </div>
                    <div className="text-xl font-bold">{status?.next_scheduled_at ? new Date(status.next_scheduled_at).toLocaleString() : '—'}</div>
                    <div className="text-xs text-neutral-500 mt-1">Retention: {status?.retention_days || 7} days · Bunny CDN</div>
                </Card>
                <Card className="p-4" data-testid="sys-card-total">
                    <div className="flex items-center gap-2 mb-1">
                        <HardDrive className="h-4 w-4 text-neutral-500" />
                        <div className="text-xs uppercase tracking-wider font-semibold text-neutral-500">Backups stored</div>
                    </div>
                    <div className="text-xl font-bold">{status?.total_backups ?? 0}</div>
                    <div className="text-xs text-neutral-500 mt-1">{status?.successful_backups ?? 0} successful</div>
                </Card>
            </div>

            <Tabs defaultValue="backups">
                <TabsList className="mb-4">
                    <TabsTrigger value="backups" data-testid="tab-backups"><DatabaseBackup className="h-4 w-4 mr-1" />Backups</TabsTrigger>
                    <TabsTrigger value="reset" data-testid="tab-reset"><AlertTriangle className="h-4 w-4 mr-1" />Data Reset</TabsTrigger>
                </TabsList>

                <TabsContent value="backups">
                    <Card className="p-4">
                        <div className="flex items-center justify-between mb-3">
                            <div>
                                <div className="text-sm font-semibold">Backup history</div>
                                <div className="text-xs text-neutral-500">Automatic backup runs daily at 00:00 UTC. Files are stored on Bunny CDN.</div>
                            </div>
                            <Button onClick={doBackup} disabled={backingUp} data-testid="sys-backup-now" style={{ background: 'hsl(142 71% 45%)', color: 'white' }}>
                                {backingUp ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <DatabaseBackup className="h-4 w-4 mr-2" />}
                                {backingUp ? 'Backing up...' : 'Backup now'}
                            </Button>
                        </div>
                        {backups.length === 0 ? (
                            <EmptyState icon={DatabaseBackup} title="No backups yet" description="Trigger a manual backup or wait for the daily scheduler." />
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="w-full text-sm" data-testid="sys-backups-table">
                                    <thead>
                                        <tr className="border-b bg-neutral-50 text-xs text-neutral-500">
                                            <th className="text-left p-2 font-medium">File</th>
                                            <th className="text-left font-medium">Created</th>
                                            <th className="text-left font-medium">Source</th>
                                            <th className="text-right font-medium">Size</th>
                                            <th className="text-right font-medium">Docs</th>
                                            <th className="text-left font-medium">Status</th>
                                            <th className="text-right p-2 font-medium">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {backups.map(b => (
                                            <tr key={b.id} className="border-b hover:bg-neutral-50" data-testid={`backup-row-${b.id}`}>
                                                <td className="p-2 font-mono text-xs">{b.file_name || '—'}</td>
                                                <td className="text-xs">{b.created_at ? new Date(b.created_at).toLocaleString() : '—'}<div className="text-neutral-500">{timeAgo(b.created_at)}</div></td>
                                                <td><span className={`text-[10px] font-semibold uppercase rounded px-2 py-0.5 ${b.source === 'auto' ? 'bg-blue-100 text-blue-800' : b.source === 'pre-reset' ? 'bg-amber-100 text-amber-800' : 'bg-green-100 text-green-800'}`}>{b.source}</span></td>
                                                <td className="text-right tabular-nums">{bytesHuman(b.size_bytes)}</td>
                                                <td className="text-right tabular-nums">{b.docs_count ?? '—'}</td>
                                                <td>
                                                    {b.status === 'success' ? (
                                                        <span className="text-green-700 text-xs inline-flex items-center gap-1"><CheckCircle2 className="h-3 w-3" />Success</span>
                                                    ) : (
                                                        <span className="text-red-700 text-xs inline-flex items-center gap-1"><XCircle className="h-3 w-3" />Failed</span>
                                                    )}
                                                </td>
                                                <td className="p-2 text-right whitespace-nowrap">
                                                    {b.url && <a href={b.url} target="_blank" rel="noopener noreferrer"><Button variant="ghost" size="icon" title="Download"><Download className="h-4 w-4" /></Button></a>}
                                                    {b.status === 'success' && (
                                                        <Button variant="ghost" size="icon" title="Restore" onClick={() => { setRestoreDialog(b); setRestorePhrase(''); }} data-testid={`backup-restore-${b.id}`}>
                                                            <RotateCcw className="h-4 w-4 text-amber-600" />
                                                        </Button>
                                                    )}
                                                    <Button variant="ghost" size="icon" title="Delete" onClick={() => doDelete(b)} data-testid={`backup-delete-${b.id}`}><Trash2 className="h-4 w-4 text-red-600" /></Button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </Card>
                </TabsContent>

                <TabsContent value="reset">
                    <Card className="p-5 max-w-3xl border-red-200 bg-red-50/40">
                        <div className="flex items-start gap-2 mb-3">
                            <AlertTriangle className="h-5 w-5 text-red-600 mt-0.5 shrink-0" />
                            <div>
                                <div className="font-semibold text-red-900">Danger zone — Selective data wipe</div>
                                <div className="text-xs text-red-700 mt-0.5">Deletes documents from the selected collections. A safety backup is automatically created just before the wipe (source=pre-reset).</div>
                            </div>
                        </div>
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                            <div className="rounded-lg border bg-white p-4">
                                <div className="flex items-center justify-between mb-2">
                                    <div className="font-semibold text-sm">Master data</div>
                                    <div className="flex items-center gap-2">
                                        <Switch checked={resetForm.reset_master} onCheckedChange={v => setResetForm(f => ({ ...f, reset_master: v }))} data-testid="reset-master-switch" />
                                        <span className="text-xs text-neutral-500">{resetForm.reset_master ? 'Selected' : ''}</span>
                                    </div>
                                </div>
                                <div className="space-y-1 text-xs">
                                    {preview && Object.entries(preview.master).map(([c, n]) => (
                                        <div key={c} className="flex justify-between border-b py-1"><span className="font-mono">{c}</span><span className="tabular-nums text-neutral-600">{n} docs</span></div>
                                    ))}
                                </div>
                            </div>
                            <div className="rounded-lg border bg-white p-4">
                                <div className="flex items-center justify-between mb-2">
                                    <div className="font-semibold text-sm">Transaction data</div>
                                    <div className="flex items-center gap-2">
                                        <Switch checked={resetForm.reset_transactions} onCheckedChange={v => setResetForm(f => ({ ...f, reset_transactions: v }))} data-testid="reset-trans-switch" />
                                        <span className="text-xs text-neutral-500">{resetForm.reset_transactions ? 'Selected' : ''}</span>
                                    </div>
                                </div>
                                <div className="space-y-1 text-xs">
                                    {preview && Object.entries(preview.transactions).map(([c, n]) => (
                                        <div key={c} className="flex justify-between border-b py-1"><span className="font-mono">{c}</span><span className="tabular-nums text-neutral-600">{n} docs</span></div>
                                    ))}
                                </div>
                            </div>
                        </div>
                        <div className="mt-4 rounded-lg border bg-neutral-50 p-3 flex items-start gap-2 text-xs">
                            <ShieldCheck className="h-4 w-4 text-green-700 mt-0.5" />
                            <div><strong>Always kept:</strong> {preview && Object.keys(preview.kept).join(', ')} (admin users, business settings, and backup history remain untouched)</div>
                        </div>
                        <div className="mt-4 flex justify-end">
                            <Button onClick={() => setResetDialog(true)} disabled={!resetForm.reset_master && !resetForm.reset_transactions} data-testid="reset-open-dialog" style={{ background: 'hsl(0 84% 45%)', color: 'white' }}>
                                <AlertTriangle className="h-4 w-4 mr-2" />Proceed to reset
                            </Button>
                        </div>
                    </Card>
                </TabsContent>
            </Tabs>

            {/* RESET CONFIRM DIALOG */}
            <Dialog open={resetDialog} onOpenChange={setResetDialog}>
                <DialogContent>
                    <DialogHeader><DialogTitle className="text-red-700 flex items-center gap-2"><AlertTriangle className="h-5 w-5" />Confirm data reset</DialogTitle></DialogHeader>
                    <div className="text-sm text-neutral-700 space-y-2">
                        <p>You are about to wipe the following data:</p>
                        <ul className="list-disc pl-5 text-xs">
                            {resetForm.reset_master && <li><strong>Master data</strong> — {preview && Object.values(preview.master).reduce((a, b) => a + b, 0)} docs across {preview && Object.keys(preview.master).length} collections</li>}
                            {resetForm.reset_transactions && <li><strong>Transactions</strong> — {preview && Object.values(preview.transactions).reduce((a, b) => a + b, 0)} docs across {preview && Object.keys(preview.transactions).length} collections</li>}
                        </ul>
                        <p className="text-xs text-neutral-500">A safety backup will be created before the wipe. Type <code className="font-mono bg-neutral-100 px-1">RESET-DHARMIK</code> to confirm.</p>
                        <Input value={resetForm.confirm_phrase} onChange={e => setResetForm(f => ({ ...f, confirm_phrase: e.target.value }))} placeholder="Type RESET-DHARMIK" data-testid="reset-confirm-input" />
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setResetDialog(false)}>Cancel</Button>
                        <Button onClick={doReset} disabled={resetForm.confirm_phrase !== 'RESET-DHARMIK'} data-testid="reset-confirm-button" style={{ background: 'hsl(0 84% 45%)', color: 'white' }}>Wipe now</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* RESTORE CONFIRM DIALOG */}
            <Dialog open={!!restoreDialog} onOpenChange={o => { if (!o) { setRestoreDialog(null); setRestorePhrase(''); } }}>
                <DialogContent>
                    <DialogHeader><DialogTitle className="text-amber-700 flex items-center gap-2"><RotateCcw className="h-5 w-5" />Restore from backup</DialogTitle></DialogHeader>
                    <div className="text-sm text-neutral-700 space-y-2">
                        <p>Restore from <strong className="font-mono">{restoreDialog?.file_name}</strong>?</p>
                        <p className="text-xs text-neutral-500">This will WIPE current data (except admin users, business settings, and backup history) and repopulate from the backup. Type <code className="font-mono bg-neutral-100 px-1">RESTORE-DHARMIK</code> to confirm.</p>
                        <Input value={restorePhrase} onChange={e => setRestorePhrase(e.target.value)} placeholder="Type RESTORE-DHARMIK" data-testid="restore-confirm-input" />
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => { setRestoreDialog(null); setRestorePhrase(''); }}>Cancel</Button>
                        <Button onClick={doRestore} disabled={restorePhrase !== 'RESTORE-DHARMIK' || restoring} data-testid="restore-confirm-button" style={{ background: 'hsl(35 90% 50%)', color: 'white' }}>
                            {restoring ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Restoring...</> : 'Restore'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </AppShell>
    );
}
